import React, { useMemo, useState } from 'react';
import { analyzeIntake, commitIntake } from '../api';

// Schema-agnostic intake: pick any CSV → land + auto-suggest a column
// mapping → confirm via dropdowns → commit to the canonical model.
const CONF = { hi: ['High', 'hi'], med: ['Likely', 'med'], none: ['Not found', 'none'] };

const STEPS = [['locations', 'Site locations'], ['sales', 'Sales']];

export default function MappingIntake({ onDone, onCancel }) {
  const [step, setStep] = useState('locations');    // 'locations' | 'sales'
  const [csv, setCsv] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [analysis, setAnalysis] = useState(null);   // server suggestion
  const [map, setMap] = useState({});               // field -> column
  const [opts, setOpts] = useState({ sales_unit: 'rand', date_format: '%Y-%m-%d', grain: 'panel' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [result, setResult] = useState(null);       // commit validation

  const resetFile = () => { setCsv(null); setFileName(null); setAnalysis(null); setMap({}); setResult(null); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMsg(null); setResult(null);
    const text = await f.text();
    setCsv(text); setFileName(f.name); setBusy(true);
    try {
      const a = await analyzeIntake(text, f.name, step);
      setAnalysis(a);
      const seed = {};
      (a.saved_mapping?.mapping) // reuse a saved mapping if one exists
        ? Object.assign(seed, a.saved_mapping.mapping)
        : a.mapping.forEach(m => { seed[m.field] = m.column; });
      setMap(seed);
      if (a.saved_mapping?.options) setOpts(o => ({ ...o, ...a.saved_mapping.options }));
    } catch (err) { setMsg(err.message || 'Could not read file'); }
    finally { setBusy(false); }
  };

  const cols = analysis?.columns || [];
  const usedCols = useMemo(() => new Set(Object.values(map).filter(Boolean)), [map]);
  const fieldByCol = useMemo(() => {
    const o = {}; (analysis?.mapping || []).forEach(m => { if (map[m.field]) o[map[m.field]] = m.label; });
    return o;
  }, [map, analysis]);
  const ignored = cols.filter(c => !usedCols.has(c));
  const missing = (analysis?.mapping || []).filter(m => m.required && !map[m.field]).map(m => m.label);
  const commitReady = analysis && missing.length === 0;

  const onCommit = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await commitIntake(csv, fileName, map, opts, step);
      setResult(r);
      if (step === 'locations') {
        setMsg(`${r.n_stores} store locations saved. Now upload your sales file.`);
        setTimeout(() => { resetFile(); setStep('sales'); setMsg(null); }, 1100);
      } else {
        setMsg(`Sales committed — ${r.n_panel_rows} rows across ${r.n_stores} stores.`);
        if (onDone) setTimeout(() => onDone(r), 1000);
      }
    } catch (err) { setMsg(err.message || 'Commit failed'); }
    finally { setBusy(false); }
  };

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <div style={S.h1}>{step === 'locations' ? 'Site locations' : 'Sales'}</div>
          <div style={S.sub}>
            {step === 'locations'
              ? 'Upload your store list — one row per store. We detect the columns; you confirm.'
              : 'Now your sales file — one row per store per period, joined to the locations by store ID.'}
          </div>
        </div>
        {analysis && <div style={S.file}><b>{fileName}</b><br />{analysis.n_rows?.toLocaleString()} rows · {cols.length} columns</div>}
      </div>

      <div style={S.stepper}>
        {STEPS.map(([k, label], i) => {
          const done = STEPS.findIndex(s => s[0] === step) > i;
          const active = step === k;
          return (
            <React.Fragment key={k}>
              <div style={S.stepItem}>
                <span style={{ ...S.stepDot, ...(active || done ? S.stepDotOn : {}) }}>{done ? '✓' : i + 1}</span>
                <span style={{ ...S.stepLbl, ...(active ? { color: 'var(--s-text)' } : {}) }}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <div style={{ ...S.stepLine, ...(done ? S.stepLineOn : {}) }} />}
            </React.Fragment>
          );
        })}
      </div>

      {!analysis && (
        <div style={S.drop}>
          <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ fontSize: 13, color: 'var(--s-muted)' }} />
          <div style={S.hint}>
            {step === 'locations'
              ? 'Any CSV — store id, location, open date, format. No template; nothing is rejected.'
              : 'Any CSV — store id, period (week/month), sales value. Store IDs must match the locations file.'}
          </div>
          {busy && <div style={S.hint}>Analysing…</div>}
          {msg && <div style={S.err}>{msg}</div>}
        </div>
      )}

      {analysis && (
        <div style={S.body}>
          <div style={S.left}>
            <div style={S.lbl}>Your columns</div>
            <div style={S.colList}>
              {cols.map(c => (
                <div key={c} style={{ ...S.col, ...(usedCols.has(c) ? S.colUsed : S.colIgn) }}>
                  <span style={{ ...S.dot, background: usedCols.has(c) ? 'var(--s-accent)' : 'var(--s-w14)' }} />
                  {c}{fieldByCol[c] && <span style={S.colTo}>→ {fieldByCol[c]}</span>}
                </div>
              ))}
            </div>
            {analysis.saved_mapping && <div style={S.saved}>↺ Reusing your saved mapping for this brand.</div>}
          </div>

          <div style={S.right}>
            <div style={S.sec}>Map to the fields we need</div>
            {analysis.mapping.map(f => {
              const miss = f.required && !map[f.field];
              const [label, cls] = CONF[map[f.field] === f.column ? f.confidence : (map[f.field] ? 'hi' : 'none')];
              return (
                <div key={f.field} style={S.row}>
                  <div style={S.fld}>{f.label}
                    <span style={{ ...S.badge, color: f.required ? 'var(--s-accent)' : 'var(--s-faint)' }}>{f.required ? 'Required' : 'Optional'}</span>
                    {f.note && <small style={S.note}>{f.note}</small>}
                  </div>
                  <span style={S.arrow}>→</span>
                  <select
                    value={map[f.field] || ''}
                    onChange={e => setMap(m => ({ ...m, [f.field]: e.target.value || null }))}
                    style={{ ...S.sel, ...(miss ? { borderColor: 'var(--s-red)' } : {}) }}
                  >
                    <option value="">— not present —</option>
                    {cols.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <span style={{ ...S.conf, ...S['c' + (miss ? 'none' : cls)] }}>
                    {miss ? 'Missing' : label}
                  </span>
                </div>
              );
            })}

            <div style={S.qs}>
              <Q label="Sales units" value={opts.sales_unit} onChange={v => setOpts(o => ({ ...o, sales_unit: v }))}
                 options={[['rand', 'Rand'], ['cents', 'Cents'], ['thousands', 'Thousands']]} />
              <Q label="Date format" value={opts.date_format} onChange={v => setOpts(o => ({ ...o, date_format: v }))}
                 options={[['%Y-%m-%d', 'YYYY-MM-DD'], ['%d/%m/%Y', 'DD/MM/YYYY'], ['%m/%d/%Y', 'MM/DD/YYYY']]} />
              <Q label="Grain" value={opts.grain} onChange={v => setOpts(o => ({ ...o, grain: v }))}
                 options={[['panel', 'Store × period'], ['cross_section', 'Row per store']]} />
            </div>

            <div style={S.sec}>Ignored — not used</div>
            <div style={S.ignRow}>
              {ignored.length ? ignored.map(c => <span key={c} style={S.ignCol}>{c}</span>)
                : <span style={S.ignCol}>none — all columns used</span>}
            </div>

            {(missing.length > 0 || result) && (
              <>
                <div style={S.sec}>Validation</div>
                <div style={S.valid}>
                  {missing.length > 0 && <div style={{ ...S.vRow, color: 'var(--s-red)' }}>✕ Unmapped required: <b>{missing.join(', ')}</b></div>}
                  {result && result.kind === 'locations' && <>
                    <div style={S.vRow}><span style={S.ok}>✓</span> <b>{result.n_stores}</b> store locations saved</div>
                    <div style={S.vRow}><span style={result.missing_coords ? S.warn : S.ok}>{result.missing_coords ? '!' : '✓'}</span> <b>{result.with_coords}</b> with coordinates{result.missing_coords ? ` · ${result.missing_coords} missing` : ''}</div>
                    {result.row_issues > 0 && <div style={S.vRow}><span style={S.warn}>!</span> {result.row_issues} rows flagged (bad date / value)</div>}
                  </>}
                  {result && result.kind === 'sales' && <>
                    <div style={S.vRow}><span style={S.ok}>✓</span> <b>{result.n_panel_rows}</b> sales rows · <b>{result.n_stores}</b> stores</div>
                    <div style={S.vRow}><span style={result.referential_ok ? S.ok : S.warn}>{result.referential_ok ? '✓' : '!'}</span> {result.referential_ok ? 'Every store ID matches a location' : `${result.orphan_store_ids} orphan store IDs — skipped`}</div>
                    {result.row_issues > 0 && <div style={S.vRow}><span style={S.warn}>!</span> {result.row_issues} rows flagged (bad date / sales ≤ 0)</div>}
                    <div style={S.vRow}><span style={S.ok}>✓</span> {result.grain === 'panel' ? `Panel — up to ${result.max_periods} periods/store` : 'Cross-section — one row per store'}</div>
                  </>}
                </div>
              </>
            )}

            {msg && <div style={result ? S.okmsg : S.err}>{msg}</div>}

            <div style={S.foot}>
              <button className="sq-btn" onClick={onCancel}>Cancel</button>
              <button className="sq-btn primary" onClick={onCommit} disabled={!commitReady || busy}>
                {busy ? 'Committing…' : 'Confirm mapping & commit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Q({ label, value, onChange, options }) {
  return (
    <div>
      <label style={S.qLbl}>{label}</label>
      <select className="sq-select" value={value} onChange={e => onChange(e.target.value)} style={{ width: '100%' }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '4px 0 16px', borderBottom: '1px solid var(--s-line)' },
  h1: { fontFamily: 'var(--s-serif)', fontSize: 20, fontWeight: 700 },
  sub: { fontSize: 12.5, color: 'var(--s-muted)', marginTop: 4, maxWidth: 460 },
  file: { font: '12px var(--s-mono)', color: 'var(--s-faint)', textAlign: 'right', lineHeight: 1.7 },
  stepper: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0 4px' },
  stepItem: { display: 'flex', alignItems: 'center', gap: 9 },
  stepDot: { width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '600 11px var(--s-sans)', background: 'var(--s-w06)', border: '1px solid var(--s-line)', color: 'var(--s-faint)', flexShrink: 0 },
  stepDotOn: { background: 'var(--s-accent)', borderColor: 'var(--s-accent)', color: '#0a0a0a' },
  stepLbl: { font: '600 12px var(--s-sans)', color: 'var(--s-faint)' },
  stepLine: { flex: 1, height: 1, background: 'var(--s-line)', maxWidth: 90 },
  stepLineOn: { background: 'var(--s-accent)' },
  drop: { padding: '30px 0', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' },
  hint: { fontSize: 11.5, color: 'var(--s-faint)' },
  err: { fontSize: 12.5, color: 'var(--s-red)' },
  okmsg: { fontSize: 12.5, color: 'var(--s-green)', marginTop: 12 },
  body: { display: 'grid', gridTemplateColumns: '250px 1fr', gap: 0, marginTop: 4 },
  left: { borderRight: '1px solid var(--s-line)', paddingRight: 20, paddingTop: 18 },
  right: { paddingLeft: 22, paddingTop: 18 },
  lbl: { font: '600 10px var(--s-sans)', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--s-faint)', marginBottom: 10 },
  colList: { display: 'flex', flexDirection: 'column', gap: 2 },
  col: { display: 'flex', alignItems: 'center', gap: 8, font: '11.5px var(--s-mono)', padding: '5px 8px', borderRadius: 7 },
  colUsed: { color: 'var(--s-text)' }, colIgn: { color: 'var(--s-faint)' },
  dot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  colTo: { color: 'var(--s-faint)', marginLeft: 'auto', fontSize: 10 },
  saved: { marginTop: 14, fontSize: 11.5, color: 'var(--s-accent)' },
  sec: { font: '600 10px var(--s-sans)', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--s-muted)', margin: '18px 0 10px', display: 'flex', alignItems: 'center', gap: 10 },
  row: { display: 'grid', gridTemplateColumns: '170px 20px 1fr 92px', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--s-line2)' },
  fld: { fontSize: 13, fontWeight: 600, color: 'var(--s-text)' },
  badge: { font: '600 8px var(--s-sans)', letterSpacing: '0.08em', textTransform: 'uppercase', marginLeft: 7 },
  note: { display: 'block', fontSize: 11, color: 'var(--s-faint)', fontWeight: 400, marginTop: 1 },
  arrow: { color: 'var(--s-faint)', textAlign: 'center' },
  sel: { appearance: 'none', width: '100%', background: 'var(--s-w05)', border: '1px solid var(--s-line)', color: 'var(--s-text)', font: '500 12.5px var(--s-mono)', padding: '8px 11px', borderRadius: 9, cursor: 'pointer' },
  conf: { font: '600 9.5px var(--s-sans)', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '4px 9px', borderRadius: 99, textAlign: 'center' },
  chi: { background: 'rgba(61,220,132,0.13)', color: 'var(--s-green)' },
  cmed: { background: 'rgba(255,176,46,0.13)', color: 'var(--s-amber)' },
  cnone: { background: 'rgba(255,93,93,0.13)', color: 'var(--s-red)' },
  cNone: { background: 'rgba(255,93,93,0.13)', color: 'var(--s-red)' },
  qs: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, margin: '14px 0 4px' },
  qLbl: { display: 'block', font: '600 10px var(--s-sans)', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--s-faint)', marginBottom: 6 },
  ignRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  ignCol: { font: '11.5px var(--s-mono)', color: 'var(--s-faint)', background: 'var(--s-w04)', border: '1px solid var(--s-line2)', padding: '4px 9px', borderRadius: 7 },
  valid: { border: '1px solid var(--s-line)', borderRadius: 11, padding: '13px 15px', background: 'var(--s-w02)' },
  vRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--s-muted)', padding: '3px 0' },
  ok: { color: 'var(--s-green)' }, warn: { color: 'var(--s-amber)' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 },
};
